/**
 * Modifies a Trix editor for ESD.
 *
 * Example: %div{ data: {controller: "trix--editor"} }
 *
 */
import { Controller } from "@hotwired/stimulus";
import Trix from "trix";

export default class extends Controller {
  static values = { fileSizeWarning: String, maxLength: Number, textOnly: Boolean };

  get currentForm() {
    return this.element.closest("form");
  }

  connect() {
    Trix.config.toolbar.getDefaultHTML = this.toolbarDefaultHTML.bind(this);
    Trix.config.lang.urlPlaceholder = "Enter a URL starting with https:// or http:// ...";
    // Initialize when Trix editor is ready
    this.element.addEventListener("trix-initialize", this.initializeEditor.bind(this), {
      once: true,
    });
  }

  initializeEditor() {
    this.updateToolbars();
    this.createStatusBar();
    this.createCharacterCounter();
    this.updateCharacterCounter();
    this.setupEventListeners();
    this.element.focus();
  }

  setupEventListeners() {
    // Find the href input and embed elements in the toolbar
    this.hrefInput = this.toolbar?.querySelector("[data-trix-input][name='href']");
    this.embedButton = this.toolbar?.querySelector("[data-behavior='embed_url']");

    // Add event listeners
    if (this.hrefInput) {
      this.hrefInput.addEventListener("input", this.handleUrlInput.bind(this));
      this.hrefInput.addEventListener("focusin", this.handleUrlInput.bind(this));
    }

    if (this.embedButton) {
      this.embedButton.addEventListener("click", this.embedMedia.bind(this));
    }

    this.element.addEventListener("trix-file-accept", (event) => {
      const maxFileSize = 15 * 1024 ** 2; // 15MB
      if (event.file.size > maxFileSize) {
        event.preventDefault();
        alert(this.fileSizeWarningValue || "The selected file is too large.");
      }
    });

    this.element.addEventListener("trix-change", () => {
      this.updateCharacterCounter();
    });

    /*
    Turn lines starting with dashes or stars into bullet lists and lines starting with "1. " into numbered lists.
    Works within nested lists but skips headings, code blocks and pasted text.
    https://github.com/basecamp/trix/issues/644
    */
    this.element.addEventListener("trix-change", function (event) {
      const editor = event.target.editor;
      const position = editor.getPosition();
      if (
        position < 2 ||
        editor.attributeIsActive("code") ||
        editor.attributeIsActive("heading1")
      ) {
        return;
      }
      let text = editor
        .getDocument()
        .toString()
        .substring(position - 4, position);
      const bullet = /\n[*-] /;
      const number = /\n1\. /;
      let type,
        offset = null;
      if (position < 4) {
        text = "\n" + text;
      }

      if (bullet.test(text)) {
        offset = 2;
        type = "bullet";
      } else if (number.test(text)) {
        offset = 3;
        type = "number";
      }
      if (type) {
        editor.recordUndoEntry("autolist");
        editor.setSelectedRange([position - offset, position]);
        editor.deleteInDirection("forward");
        editor.activateAttribute(type);
      }
    });

    // Attach file button
    if (this.currentForm) {
      const messageAddButton = this.currentForm.querySelector(".trix-attach-file-btn");
      if (messageAddButton) {
        messageAddButton.addEventListener("click", () => {
          this.attachFile();
        });
        messageAddButton.addEventListener("keydown", (event) => {
          if (event.keyCode !== 13) return;
          this.attachFile();
        });
      }
    }
  }

  updateToolbars() {
    const toolbar = this.element.parentElement.querySelector("trix-toolbar");
    const html = Trix.config.toolbar.getDefaultHTML();

    toolbar.innerHTML = html;
  }

  toolbarDefaultHTML() {
    const { lang } = Trix.config;

    // Conditionally include link and file attachment buttons
    const linkButton = this.textOnlyValue
      ? ""
      : `<button type="button" class="trix-button trix-button--icon trix-button--icon-link" data-trix-attribute="href" data-trix-action="link" data-trix-key="k" title="${lang.link}" tabindex="-1">${lang.link}</button>`;
    const fileButtons = this.textOnlyValue
      ? false
      : `<button type="button" class="trix-button trix-button--icon trix-button--icon-file" data-trix-action="attachFiles" title="Attach Images" tabindex="-1"><i class="fas fa-image"></i></button>
          <button type="button" class="trix-button trix-button--icon trix-button--icon-file" data-trix-action="attachFiles" title="${lang.attachFiles}" tabindex="-1"><i class="far fa-file"></i></button>`;
    const linkDialog = this.textOnlyValue
      ? false
      : `<div class="trix-dialog trix-dialog--link" data-trix-dialog="href" data-trix-dialog-attribute="href">
          <div class="trix-dialog__link-fields">
            <input type="url" name="href" class="trix-input trix-input--dialog" placeholder="${lang.urlPlaceholder}" aria-label="${lang.url}" required data-trix-input disabled="disabled">
            <div class="trix-button-group">
              <input type="button" class="trix-button trix-button--dialog" value="${lang.link}" data-trix-method="setAttribute">
              <input type="button" class="trix-button trix-button--dialog" value="${lang.unlink}" data-trix-method="removeAttribute">
            </div>
          </div>
        </div>`;

    return `
      <div class="trix-button-row gap-2">
        <span class="p-2 trix-button-group trix-button-group--text-tools" data-trix-button-group="text-tools">
          <button type="button" class="trix-button trix-button--icon trix-button--icon-bold" data-trix-attribute="bold" data-trix-key="b" title="${lang.bold}" tabindex="-1">${lang.bold}</button>
          <button type="button" class="trix-button trix-button--icon trix-button--icon-italic" data-trix-attribute="italic" data-trix-key="i" title="${lang.italic}" tabindex="-1">${lang.italic}</button>
          <button type="button" class="trix-button trix-button--icon trix-button--icon-bullet-list" data-trix-attribute="bullet" title="${lang.bullets}" tabindex="-1">${lang.bullets}</button>
          <button type="button" class="trix-button trix-button--icon trix-button--icon-number-list" data-trix-attribute="number" title="${lang.numbers}" tabindex="-1">${lang.numbers}</button>
          ${linkButton}
        </span>

        ${
          fileButtons
            ? `<span class="p-2 trix-button-group trix-button-group--file-tools" data-trix-button-group="file-tools">${fileButtons}</span>`
            : ""
        }
        

        <span class="trix-button-group-spacer"></span>

        <span class="p-2 trix-button-group trix-button-group--history-tools" data-trix-button-group="history-tools">
          <button type="button" class="trix-button trix-button--icon trix-button--icon-undo" data-trix-action="undo" data-trix-key="z" title="${lang.undo}" tabindex="-1">${lang.undo}</button>
          <button type="button" class="trix-button trix-button--icon trix-button--icon-redo" data-trix-action="redo" data-trix-key="shift+z" title="${lang.redo}" tabindex="-1">${lang.redo}</button>
        </span>
      </div>

      ${
        linkDialog
          ? `<div class="trix-dialogs" data-trix-dialogs>
            ${linkDialog}
          </div>`
          : ""
      }
    `;
  }

  attachFile() {
    this.element.editorController.invokeAction("attachFiles");
  }

  createStatusBar() {
    if (this.statusBarElement) {
      return;
    }

    const statusBar = document.createElement("div");
    statusBar.className = "trix-editor-status-bar";
    this.element.insertAdjacentElement("afterend", statusBar);
    this.statusBarElement = statusBar;

    this.element.dispatchEvent(new Event("trix-status-bar-initialize"));
  }

  createCharacterCounter() {
    if (!this.hasMaxLengthValue || this.counterElement) {
      return;
    }

    const counter = document.createElement("span");
    counter.className = "character-counter";
    this.statusBarElement.appendChild(counter);
    this.counterElement = counter;
  }

  updateCharacterCounter() {
    if (!this.counterElement) {
      return;
    }

    const editor = this.element.editor;
    if (!editor) {
      return;
    }

    const string = editor.getDocument().toString();
    const characterCount = string.length - 1; // Subtract 1 for trailing newline

    if (this.hasMaxLengthValue && characterCount > this.maxLengthValue) {
      const excess = characterCount - this.maxLengthValue;
      const doc = editor.getDocument();
      const length = doc.getLength();

      // Remove excess characters from the end
      editor.setSelectedRange([length - excess - 1, length - 1]);
      editor.deleteInDirection("forward");

      const newString = editor.getDocument().toString();
      const newCount = newString.length - 1;
      this.displayCharacterCount(newCount);
    } else {
      this.displayCharacterCount(characterCount);
    }
  }

  displayCharacterCount(count) {
    if (!this.counterElement) {
      return;
    }

    const maxLength = this.maxLengthValue;
    const percentage = (count / maxLength) * 100;

    this.counterElement.textContent = `${count} / ${maxLength} characters`;

    if (percentage >= 100) {
      this.counterElement.classList.remove("limit-close");
      this.counterElement.classList.add("limit-reached");
    } else if (percentage >= 80) {
      this.counterElement.classList.remove("limit-reached");
      this.counterElement.classList.add("limit-close");
    } else {
      this.counterElement.classList.remove("limit-reached");
      this.counterElement.classList.remove("limit-close");
    }
  }
}
